/**
 * Pure function: decides whether a loaded course belongs to the user's
 * current organization. Defence-in-depth — the backend also enforces
 * isolation.
 *
 * Returns:
 * - `{ allowed: true }` when the course is accessible
 * - `{ allowed: false, reason: string }` when the modal should close
 */
export interface OrgAccessUser {
  isSystemAdmin?: boolean;
}

export interface OrgAccessInputs {
  courseData: { organization_id?: string; organizationId?: string } | null;
  currentOrgId: string | null;
  currentUser: OrgAccessUser | null;
}

export type OrgAccessResult =
  | { allowed: true }
  | { allowed: false; reason: string };

export function decideCourseOrgAccess({
  courseData,
  currentOrgId,
  currentUser
}: OrgAccessInputs): OrgAccessResult {
  if (!courseData) {
    return { allowed: true };
  }

  const courseOrgId = courseData.organization_id || courseData.organizationId;

  // If we can't determine either org, allow — the backend enforces isolation too.
  if (!currentOrgId || !courseOrgId) {
    return { allowed: true };
  }

  if (courseOrgId === currentOrgId) {
    return { allowed: true };
  }

  // System admins can access all orgs.
  if (currentUser?.isSystemAdmin) {
    return { allowed: true };
  }

  return {
    allowed: false,
    reason: `Course org mismatch. Course org: ${courseOrgId}, current org: ${currentOrgId}.`
  };
}
